import type { ReactNode } from "react";
import { BuildingLibraryIcon, CheckIcon } from "@heroicons/react/24/outline";

import { formatCurrency } from "helpers/currency";
import { formatPhoneNumber } from "helpers/phone";
import { PHONE } from "constants/at-need";

import {
  useArrangementState,
  useArrangementActions,
} from "./ArrangementContext";
import { STEP_ROUTES } from "./constants";
import type { PackageSection } from "./screenModel";
import type {
  ArrangementConfig,
  PackageInclusions,
  QuoteBreakdown,
  ServiceAnswer,
} from "./types";
import {
  YES_ONLY_SERVICE_LABELS,
  computeServiceAnswerCost,
  visibleInclusionHighlights,
  visiblePassThroughFeeAnswers,
  getIncludedOfferingQuantity,
  extractQuoteFees,
  quoteSubtotal,
  filterVisibleServiceAnswers,
} from "./helpers";
import { ENGRAVING_PRICE } from "./engravingConstants";
import GplLink from "./GplLink";
import { isMeaningfulVariantName } from "./variantLabels";

export default function StatementOfGoods({
  readOnly = false,
}: {
  readOnly?: boolean;
}) {
  const {
    state,
    packageInclusions,
    config,
    quoteBreakdown,
    quoteTotal,
    isQuoteLoading,
  } = useArrangementState();

  const packages = config?.packages ?? [];
  const urns = config?.urns ?? [];
  const keepsakes = config?.keepsakes ?? [];
  const serviceQuestions = config?.service_questions ?? [];
  const addOns = config?.add_ons ?? [];

  const selectedPackage = packages.find(
    (p) => p.variant_id === state.selectedPackageId,
  );
  const includedUrn = urns.find((u) => u.id === state.includedUrnId);
  const includedVariant = includedUrn?.variants.find(
    (v) => v.id === state.includedUrnVariantId,
  );
  const additionalUrnDetails = state.additionalUrns.map((sel) => {
    const urn = urns.find((u) => u.id === sel.urnId);
    const variant = urn?.variants.find((v) => v.id === sel.variantId);
    return { ...sel, urn, variant };
  });

  // Split service answers into After services vs government fees
  const afterServiceAnswers = filterVisibleServiceAnswers(
    state.serviceAnswers,
    serviceQuestions,
  );
  const govFeeAnswers = visiblePassThroughFeeAnswers(state.serviceAnswers);
  const inclusionHighlights = visibleInclusionHighlights(
    selectedPackage?.external.inclusion_highlights ?? [],
    state.serviceAnswers,
  );

  return (
    <div>
      {/* Cremation Package */}
      <Section title="Cremation Package" onEdit="packages" readOnly={readOnly}>
        {selectedPackage ? (
          <div>
            <div className="flex justify-between">
              <span className="text-lg font-medium text-evergreen">
                {selectedPackage.title} Package
              </span>
              <span className="text-lg font-medium text-evergreen">
                {formatCurrency(parseFloat(selectedPackage.price))}
              </span>
            </div>
            {/* Package contents are attributes of the plan, not separate line
                items the family selected. Frame them as a contained, labeled
                group with check icons and lighter type so they read as
                "what comes with this package" rather than chosen goods. */}
            {inclusionHighlights.length > 0 && (
              <div className="mt-3 rounded-md border border-oat bg-oat/30 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-soil/70">
                  Included in this package
                </p>
                <ul className="mt-2 space-y-1.5">
                  {inclusionHighlights.map((inc) => (
                    <li
                      key={inc.title}
                      className="flex items-start gap-2 text-base text-soil"
                    >
                      <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-sage" />
                      <span>{inc.title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="mt-3 text-sm font-bold text-soil">
              Fees NOT included in package: third-party cremation fees, death
              certificate filing fees, and any additional services selected
              below.
            </p>
          </div>
        ) : (
          <p className="text-sm text-soil">No package selected</p>
        )}
      </Section>

      {/* Urns & Keepsakes */}
      <Section title="Urns & Keepsakes" onEdit="packages" readOnly={readOnly}>
        {/* Included urn */}
        {includedUrn && (
          <>
            <LineItem
              name={
                <>
                  {includedUrn.name}
                  {includedVariant &&
                    isMeaningfulVariantName(includedVariant.name) && (
                      <span className="text-soil">
                        {" "}
                        - {includedVariant.name}
                      </span>
                    )}
                </>
              }
              note="(Included)"
              value="Included"
            />
            {state.includedUrnEngraving && (
              <div className="mt-1 flex justify-between pl-4">
                <span className="text-base text-soil">Engraving</span>
                <span className="text-base font-medium text-evergreen">
                  {formatCurrency(ENGRAVING_PRICE)}
                </span>
              </div>
            )}
          </>
        )}

        {/* Additional urns — engraving is rolled into the unit price and
            surfaced via a "w/ engraving" suffix so the line matches the SFG,
            which also shows engraving bundled into the urn's line. */}
        {additionalUrnDetails.map(
          ({ urn, variant, quantity, urnId, engraving }) => {
            if (!urn) return null;
            const basePrice = variant?.price ?? urn.price;
            const unitPrice = basePrice + (engraving ? ENGRAVING_PRICE : 0);
            return (
              <LineItem
                key={urnId}
                className="mt-2"
                name={
                  <>
                    {urn.name}
                    {variant && isMeaningfulVariantName(variant.name) && (
                      <span className="text-soil"> - {variant.name}</span>
                    )}
                    {engraving && (
                      <span className="text-soil"> w/ engraving</span>
                    )}
                    {quantity > 1 && (
                      <span className="text-soil"> x{quantity}</span>
                    )}
                  </>
                }
                value={formatCurrency(unitPrice * quantity)}
              />
            );
          },
        )}

        {/* Keepsakes */}
        {state.selectedKeepsakes.map((sel) => {
          const keepsake = keepsakes.find((k) => k.id === sel.productId);
          if (!keepsake) return null;
          const variant = keepsake.variants?.find(
            (v) => v.id === sel.variantId,
          );
          const unitPrice = variant?.price ?? keepsake.price;
          return (
            <LineItem
              key={sel.productId}
              className="mt-2"
              name={
                <>
                  {keepsake.name}
                  {variant && isMeaningfulVariantName(variant.name) && (
                    <span className="text-soil"> - {variant.name}</span>
                  )}
                  {sel.quantity > 1 && (
                    <span className="text-soil"> x{sel.quantity}</span>
                  )}
                </>
              }
              value={formatCurrency(unitPrice * sel.quantity)}
            />
          );
        })}

        {!includedUrn &&
          additionalUrnDetails.length === 0 &&
          state.selectedKeepsakes.length === 0 && (
            <p className="text-sm text-soil">No urn selected</p>
          )}
      </Section>

      {/* Additional Services */}
      <Section
        title="Additional Services"
        onEdit="services"
        readOnly={readOnly}
      >
        {afterServiceAnswers.length > 0 ? (
          afterServiceAnswers.map((answer) =>
            renderServiceAnswer(answer, packageInclusions, serviceQuestions),
          )
        ) : (
          <p className="text-sm text-soil">No additional services selected</p>
        )}
      </Section>

      {/* Add-On Services */}
      {state.addOnSelections.length > 0 && (
        <Section
          title="Add-On Services"
          onEdit="packages"
          section="add-ons"
          readOnly={readOnly}
        >
          {state.addOnSelections.map((sel) => {
            const service = addOns.find((s) => s.id === sel.serviceId);
            if (!service || service.informationalOnly) return null;
            const includedQty = getIncludedOfferingQuantity(
              packageInclusions,
              service.offeringId,
            );
            const chargeableQty = Math.max(0, sel.quantity - includedQty);
            const amount = service.pricing.amount * chargeableQty;
            const isFullyIncluded = chargeableQty === 0 && includedQty > 0;
            return (
              <LineItem
                key={sel.serviceId}
                className="mt-2"
                name={
                  <>
                    {service.name}
                    {sel.quantity > 1 && (
                      <span className="text-soil"> x{sel.quantity}</span>
                    )}
                  </>
                }
                note={includedQty > 0 ? `(${includedQty} included)` : undefined}
                value={isFullyIncluded ? "Included" : formatCurrency(amount)}
              />
            );
          })}
        </Section>
      )}

      {/* Government & Filing Fees */}
      {govFeeAnswers.length > 0 && (
        <GovFeesSection
          govFeeAnswers={govFeeAnswers}
          packageInclusions={packageInclusions}
          config={config}
          readOnly={readOnly}
        />
      )}

      {/* Summary totals. No top border here — the preceding section already
          draws a bottom rule, so a border-t would double it up. */}
      <div className="mt-8 space-y-3">
        {/* TODO: Extract inline spinner SVG to a shared Spinner component (same SVG exists in Button.tsx) */}
        {isQuoteLoading ? (
          <div className="flex items-center justify-center py-4 text-base text-soil">
            <svg
              className="mr-2 h-4 w-4 animate-spin text-evergreen"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            Calculating...
          </div>
        ) : (
          <QuoteTotals
            quoteBreakdown={quoteBreakdown}
            quoteTotal={quoteTotal}
          />
        )}
        <div className="space-y-1 pt-2">
          <GplLink />
          <p className="text-base text-soil">
            Questions? Call us at{" "}
            <span className="font-semibold">{formatPhoneNumber(PHONE)}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * One Statement-of-Goods line: an item name on the left (muted suffixes like
 * variant / quantity / "w/ engraving" are baked into `name` by the caller), an
 * optional de-emphasized "(included)" note, and a right-aligned value (a price
 * or "Included"). Single implementation shared by every section so the type
 * sizing and the included-indicator color stay consistent.
 *
 * Sized text-base on mobile (matching the fee-breakdown rows) and text-lg on
 * desktop. The included note uses the lighter `sage` everywhere.
 */
function LineItem({
  name,
  note,
  value,
  className = "",
}: {
  name: ReactNode;
  note?: ReactNode;
  value: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`flex justify-between gap-3 text-base sm:text-lg ${className}`}
    >
      <span className="text-evergreen">
        {name}
        {note && <span className="whitespace-nowrap text-sage"> {note}</span>}
      </span>
      <span className="shrink-0 font-medium text-evergreen">{value}</span>
    </div>
  );
}

function renderServiceAnswer(
  answer: ServiceAnswer,
  packageInclusions: PackageInclusions | null,
  serviceQuestions: ArrangementConfig["service_questions"],
) {
  const question = serviceQuestions.find((q) => q.id === answer.questionId);
  if (!question) return null;

  const cost = computeServiceAnswerCost(answer, question, packageInclusions);

  if (question.type === "radio" && answer.selectedOptionId) {
    const option = question.options?.find(
      (o) => o.id === answer.selectedOptionId,
    );
    if (!option) return null;
    const isIncluded =
      !!option.offeringId &&
      getIncludedOfferingQuantity(packageInclusions, option.offeringId) > 0;
    const displayLabel = YES_ONLY_SERVICE_LABELS[question.id] ?? option.label;
    return (
      <LineItem
        key={answer.questionId}
        className="mt-2"
        name={displayLabel}
        note={isIncluded ? "(Included)" : undefined}
        value={cost === 0 ? "Included" : formatCurrency(cost)}
      />
    );
  }

  if (
    question.type === "stepper" &&
    answer.quantity &&
    answer.quantity > 0 &&
    question.stepperConfig
  ) {
    const included = packageInclusions?.includedDeathCerts ?? 0;
    return (
      <LineItem
        key={answer.questionId}
        className="mt-2"
        name={`Death Certificates x${answer.quantity}`}
        note={included > 0 ? `(${included} included)` : undefined}
        value={cost === 0 ? "Included" : formatCurrency(cost)}
      />
    );
  }

  return null;
}

function GovFeesSection({
  govFeeAnswers,
  packageInclusions,
  config,
  readOnly = false,
}: {
  govFeeAnswers: ServiceAnswer[];
  packageInclusions: PackageInclusions | null;
  config: ArrangementConfig | null;
  readOnly?: boolean;
}) {
  const serviceQuestions = config?.service_questions ?? [];

  return (
    <div className="mb-8 border-b border-oat pb-6">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BuildingLibraryIcon className="h-5 w-5 text-soil" />
          <h3 className="text-xl font-bold text-evergreen">
            Government & Filing Fees
          </h3>
        </div>
        <EditLink step="services" label={readOnly ? "View" : undefined} />
      </div>
      <p className="mb-3 text-base text-soil">
        These fees are collected on your behalf and paid directly to the issuing
        authority.
      </p>
      {govFeeAnswers.map((answer) =>
        renderServiceAnswer(answer, packageInclusions, serviceQuestions),
      )}
    </div>
  );
}

function EditLink({
  step,
  section,
  label,
}: {
  step: string;
  section?: PackageSection;
  label?: string;
}) {
  const { goToStep } = useArrangementActions();
  const stepIndex = STEP_ROUTES.indexOf(step as (typeof STEP_ROUTES)[number]);

  return (
    <button
      type="button"
      onClick={() => goToStep(stepIndex >= 0 ? stepIndex : 0, section)}
      className="rounded-md border border-evergreen px-3 py-1 text-base font-medium text-evergreen transition hover:bg-evergreen hover:text-white"
    >
      {label || "Edit"}
    </button>
  );
}

function Section({
  title,
  onEdit,
  section,
  readOnly = false,
  children,
}: {
  title: string;
  onEdit: string;
  section?: PackageSection;
  readOnly?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="mb-8 border-b border-oat pb-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xl font-bold text-evergreen">{title}</h3>
        <EditLink
          step={onEdit}
          section={section}
          label={readOnly ? "View" : "Edit"}
        />
      </div>
      {children}
    </div>
  );
}

function QuoteTotals({
  quoteBreakdown,
  quoteTotal,
}: {
  quoteBreakdown: QuoteBreakdown | null;
  quoteTotal: number;
}) {
  const {
    items: feeItems,
    specialFees,
    stateCountyFees,
    credits,
    creditTotal,
  } = extractQuoteFees(quoteBreakdown);
  const subtotal = quoteSubtotal(quoteTotal, {
    specialFees,
    stateCountyFees,
    creditTotal,
  });

  return (
    <>
      <div className="flex justify-between py-1 text-base">
        <span className="text-soil">Subtotal</span>
        <span className="text-evergreen">{formatCurrency(subtotal)}</span>
      </div>
      {feeItems.map((fee) => (
        <div key={fee.label} className="flex justify-between py-1 text-base">
          <span className="text-soil">{fee.label}</span>
          <span className="text-evergreen">{formatCurrency(fee.amount)}</span>
        </div>
      ))}
      {stateCountyFees > 0 && (
        <div className="flex justify-between py-1 text-base">
          <span className="text-soil">State & County Fees</span>
          <span className="text-evergreen">
            {formatCurrency(stateCountyFees)}
          </span>
        </div>
      )}
      {credits.map((credit) => (
        <div key={credit.label} className="flex justify-between py-1 text-base">
          <span className="text-soil">{credit.label}</span>
          <span className="text-evergreen">
            {formatCurrency(-credit.amount)}
          </span>
        </div>
      ))}
      <div className="flex justify-between py-1 text-base">
        <span className="text-soil">Tax</span>
        <span className="text-evergreen">{formatCurrency(0)}</span>
      </div>
      <div className="flex justify-between border-t-2 border-evergreen pt-3 text-2xl font-bold">
        <span className="text-evergreen">Order Total</span>
        <span className="text-evergreen">{formatCurrency(quoteTotal)}</span>
      </div>
    </>
  );
}
